import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: () => void;
    _ytApiLoaded: boolean;
    _ytApiCallbacks: (() => void)[];
  }
}

function loadYouTubeAPI(): Promise<void> {
  return new Promise((resolve) => {
    if (window.YT && window.YT.Player) { resolve(); return; }
    if (!window._ytApiCallbacks) window._ytApiCallbacks = [];
    window._ytApiCallbacks.push(resolve);
    if (!window._ytApiLoaded) {
      window._ytApiLoaded = true;
      const prevReady = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (prevReady) prevReady();
        (window._ytApiCallbacks || []).forEach((cb) => cb());
        window._ytApiCallbacks = [];
      };
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(tag);
    }
  });
}

interface YouTubeEmbedProps { videoId: string; title: string; }

function YouTubeEmbed({ videoId, title }: YouTubeEmbedProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const playerContainerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<any>(null);
  const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;

  useEffect(() => {
    if (!isPlaying) {
      if (playerRef.current) {
        try { playerRef.current.stopVideo(); } catch { /* ignore */ }
        try { playerRef.current.destroy(); } catch { /* ignore */ }
        playerRef.current = null;
      }
      if (playerContainerRef.current) {
        while (playerContainerRef.current.firstChild)
          playerContainerRef.current.removeChild(playerContainerRef.current.firstChild);
      }
      return;
    }
    loadYouTubeAPI().then(() => {
      if (!playerContainerRef.current) return;
      const div = document.createElement('div');
      playerContainerRef.current.appendChild(div);
      playerRef.current = new window.YT.Player(div, {
        videoId, width: '100%', height: '100%',
        playerVars: { autoplay: 1, rel: 0, modestbranding: 1 },
        events: {
          onStateChange: (event: any) => {
            if (event.data === window.YT.PlayerState.ENDED) setIsPlaying(false);
          },
        },
      });
    });
    return () => {
      if (playerRef.current) { try { playerRef.current.destroy(); } catch { /* ignore */ } playerRef.current = null; }
    };
  }, [isPlaying, videoId]);

  return (
    <div className="w-full h-full relative">
      <div
        className={`absolute inset-0 cursor-pointer group transition-opacity duration-300 ${isPlaying ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
        onClick={() => setIsPlaying(true)}
      >
        <img src={thumbnailUrl} alt={title} className="w-full h-full object-cover"
          onError={(e) => { (e.target as HTMLImageElement).src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`; }} />
        <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center">
          <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center bg-red-600 rounded-full shadow-lg group-hover:scale-110 transition-transform duration-300">
            <i className="ri-play-fill text-white text-2xl md:text-3xl ml-1"></i>
          </div>
        </div>
      </div>
      <div ref={playerContainerRef}
        className={`absolute inset-0 transition-opacity duration-300 ${isPlaying ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} />
    </div>
  );
}

export default function Innovation() {
  const { t } = useTranslation();

  const comparisonKeys = [
    'innovation_c0', 'innovation_c1', 'innovation_c2', 'innovation_c3',
    'innovation_c4', 'innovation_c5', 'innovation_c6',
  ];

  return (
    <section id="innovation" className="pb-16 md:pb-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Hero banner */}
        <div className="mb-12 md:mb-20">
          <div className="relative w-full h-52 md:h-96 rounded-lg overflow-hidden">
            <img
              src="https://static.readdy.ai/image/17b7b4fe0c11dd66d6ca2f4f9437a472/8155f3204d3d8750845c8193e0b1a2ed.png"
              alt="K-Grill183 Innovation System"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 md:px-8">
              <h2
                className="text-xl md:text-4xl lg:text-5xl font-bold text-white mb-3 md:mb-5 leading-tight"
                style={{ fontFamily: 'Noto Sans KR, sans-serif', wordBreak: 'keep-all' }}
              >
                {t('innovation_hero_title')}
              </h2>
              <div className="w-10 md:w-12 h-1 bg-teal-400 mx-auto mb-3 md:mb-5"></div>
              <p
                className="text-xs md:text-base lg:text-xl text-white/85 leading-relaxed whitespace-pre-line"
                style={{ fontFamily: 'Noto Sans KR, sans-serif', wordBreak: 'keep-all' }}
              >
                {t('innovation_hero_sub')}
              </p>
            </div>
          </div>
        </div>

        {/* Comparison table */}
        <div className="bg-white rounded-lg overflow-hidden mb-10 md:mb-16">
          <div className="px-4 md:px-10 pt-6 md:pt-10 pb-4 md:pb-6 text-center">
            <h2
              className="text-xl md:text-3xl font-bold text-gray-900"
              style={{ fontFamily: 'Noto Sans KR, sans-serif' }}
            >
              {t('innovation_table_title')}
            </h2>
            <div className="w-10 h-1 bg-teal-500 mt-2 md:mt-3 mx-auto"></div>
          </div>

          {/* Scrollable table on mobile */}
          <div className="overflow-x-auto">
            <div className="min-w-[600px] px-4 md:px-10 pb-6 md:pb-10">
              <div className="grid grid-cols-3 gap-4 md:gap-8">
                {/* Column 1: Category */}
                <div>
                  <h3
                    className="text-base md:text-2xl font-bold text-gray-900 mb-5 md:mb-8 pb-3 md:pb-4 border-b-2 border-gray-200"
                    style={{ fontFamily: 'Noto Sans KR, sans-serif' }}
                  >
                    {t('innovation_col1')}
                  </h3>
                  <div className="space-y-3 md:space-y-4">
                    {comparisonKeys.map((key) => (
                      <div key={key} className="flex items-center gap-2 md:gap-3">
                        <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-gray-300 rounded-full flex-shrink-0"></div>
                        <p className="text-xs md:text-base lg:text-lg text-gray-500 font-medium" style={{ fontFamily: 'Noto Sans KR, sans-serif' }}>
                          {t(`${key}_criteria`)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Column 2: Traditional */}
                <div>
                  <h3
                    className="text-base md:text-2xl font-bold text-gray-900 mb-5 md:mb-8 pb-3 md:pb-4 border-b-2 border-gray-200"
                    style={{ fontFamily: 'Noto Sans KR, sans-serif' }}
                  >
                    {t('innovation_col2')}
                  </h3>
                  <div className="space-y-3 md:space-y-4">
                    {comparisonKeys.map((key) => (
                      <div key={key} className="flex items-center gap-2 md:gap-3">
                        <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-gray-400 rounded-full flex-shrink-0"></div>
                        <p className="text-xs md:text-base lg:text-lg text-gray-600" style={{ fontFamily: 'Noto Sans KR, sans-serif' }}>
                          {t(`${key}_label`)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Column 3: K-Grill 183 */}
                <div>
                  <h3
                    className="text-base md:text-2xl font-bold text-gray-900 mb-5 md:mb-8 pb-3 md:pb-4 border-b-2 border-teal-500"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    {t('innovation_col3')}
                  </h3>
                  <div className="space-y-3 md:space-y-4">
                    {comparisonKeys.map((key) => (
                      <div key={key} className="flex items-center gap-2 md:gap-3">
                        <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-teal-500 rounded-full flex-shrink-0"></div>
                        <p className="text-xs md:text-base lg:text-lg text-gray-900 font-medium" style={{ fontFamily: 'Noto Sans KR, sans-serif' }}>
                          {t(`${key}_value`)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Videos */}
        <div className="grid md:grid-cols-2 gap-5 md:gap-8">
          {[
            { id: 'UQG4bFxBins', titleKey: 'innovation_video1' },
            { id: 'TYTfmtwozvM', titleKey: 'innovation_video2' },
          ].map((video) => (
            <div key={video.id} className="bg-white rounded-lg overflow-hidden">
              <div className="aspect-video">
                <YouTubeEmbed videoId={video.id} title={t(video.titleKey)} />
              </div>
              <div className="px-4 md:px-5 py-3 md:py-4 border-t border-gray-100">
                <p
                  className="text-sm md:text-base font-semibold text-gray-800 text-center"
                  style={{ fontFamily: 'Noto Sans KR, sans-serif' }}
                >
                  {t(video.titleKey)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
